// Перевод кандидата в сотрудники. Модалка — ровно тот случай, ради которого она оставлена
// (правило 4): необратимое действие, которое надо подтвердить.
//
// Необратимость здесь не в том, что нельзя удалить: карточка сотрудника заводится в реестре
// людей, получает табельный номер и попадает в табель и расчёт. Отменить это «кнопкой назад»
// нельзя — сотрудника придётся увольнять, а увольнение само по себе процедура с датой,
// причиной и закрытием назначений.
//
// Подтверждение называет ПОСЛЕДСТВИЕ, а не спрашивает «вы уверены». Уверенность человека не
// информация; «в реестре людей появится карточка, и человек попадёт в табель» — информация.
//
// Повтор безопасен: ключ повторной отправки сервер считает от карточки кандидата, и двое
// кадровиков, нажавших «Оформить» на одном человеке, получат ОДНОГО сотрудника. Это сказано и
// на экране: без этого второй кадровик боится нажать и звонит первому.

import { useState } from 'react';

import { RiLockLine, RiUserFollowLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import { useEmploymentTypesQuery } from '@/shared/org/queries';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';

import type { CandidateOut } from '../api/candidates';
import { newIdempotencyKey, usePromoteMutation } from '../queries';

/** Право на справочник типов оформления. У кадров его НЕТ — см. пояснение ниже по коду. */
const DICTIONARY_PERMISSION = 'core.dictionary.view';

export function PromoteDialog({
  candidate,
  готово,
}: {
  candidate: CandidateOut;
  /** Сошлись ли требования вкладки. Не выключает кнопку — объясняет отказ (правило 10). */
  готово: boolean;
}) {
  const [открыто, setОткрыто] = useState(false);
  // Ключ живёт столько же, сколько ФОРМА: двойное нажатие и повтор после обрыва обязаны
  // прийти с тем же ключом, иначе заведутся два сотрудника с одним паспортом.
  const [ключ] = useState(newIdempotencyKey);
  const [дата, setДата] = useState(todayIso);
  const [типОформления, setТипОформления] = useState(NO_VALUE);
  const [подтверждение, setПодтверждение] = useState('');
  const [ошибкаОснования, setОшибкаОснования] = useState<string | undefined>(undefined);

  const permissions = useAuthStore((state) => state.permissions);
  // Справочник типов оформления открыт по праву на справочники, а у кадров — той самой роли,
  // которая и оформляет, — его нет. Значит поле показывается только тем, кому сервер и правда
  // отдаст список; остальным вместо неработающего выбора стоит честная строка о том, где тип
  // проставится. Поле необязательное на сервере, поэтому оформление от этого не встаёт.
  const справочникВиден = can(permissions, DICTIONARY_PERMISSION);
  const типы = useEmploymentTypesQuery();

  const мутация = usePromoteMutation(candidate.id, ключ);
  const заблокированСписком = candidate.blacklist_result === 'exact';
  const заявлениеПриложено = candidate.application_file_id !== null;

  function оформить() {
    if (!заявлениеПриложено && подтверждение.trim() === '') {
      setОшибкаОснования(
        'Без основания оформление не пройдёт: по трудовому договору нужно приложенное заявление, по иным типам оформления — ваше подтверждение здесь.',
      );
      return;
    }
    setОшибкаОснования(undefined);
    мутация.mutate({
      hired_at: дата === '' ? null : дата,
      employment_type_id: типОформления === NO_VALUE ? null : типОформления,
      hr_confirmation: подтверждение.trim() === '' ? null : подтверждение.trim(),
    });
  }

  return (
    <Modal.Root
      open={открыто}
      onOpenChange={(следующее) => {
        setОткрыто(следующее);
        if (!следующее) {
          мутация.reset();
          setОшибкаОснования(undefined);
        }
      }}
    >
      <Modal.Trigger asChild>
        <Button.Root type='button' variant='primary' mode='filled' size='small'>
          <Button.Icon as={RiUserFollowLine} />
          Оформить сотрудником
        </Button.Root>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Header
          icon={заблокированСписком ? RiLockLine : RiUserFollowLine}
          title={заблокированСписком ? 'Оформить нельзя' : 'Оформить кандидата сотрудником?'}
          description={
            заблокированСписком
              ? 'Человек числится в едином чёрном списке точным совпадением.'
              : `${candidate.display_name}. Действие необратимое: обратной кнопки у оформления нет.`
          }
        />
        <Modal.Body>
          {заблокированСписком ? (
            <p className='text-paragraph-sm text-text-sub-600'>
              Точное совпадение с записью чёрного списка блокирует продвижение по воронке. Решение
              принимает ответственный за список, а не система: если это другой человек, запись надо
              снять либо поправить данные кандидата, и только после этого оформлять.
            </p>
          ) : (
            <div className='flex flex-col gap-3'>
              <ul className='flex flex-col gap-1.5 text-paragraph-sm text-text-sub-600'>
                <li>
                  В разделе «Люди» появится карточка сотрудника с фамилией, именем и телефоном из
                  этой карточки. Дальше человек живёт там.
                </li>
                <li>
                  Кандидат перейдёт на этап «Оформлен». Этот этап больше не меняется руками: он
                  ставится только оформлением.
                </li>
                <li>
                  Карточка кандидата остаётся: отклики, переписка и история этапов никуда не
                  деваются.
                </li>
                <li>
                  Повторное нажатие вернёт того же сотрудника, второго не заведётся — можно нажать
                  ещё раз, если непонятно, прошло ли.
                </li>
                {candidate.blacklist_result === 'possible' && (
                  <li>
                    По списку было возможное совпадение. Оформление пройдёт, но подтверждение
                    останется в журнале сверок вместе с вашим именем.
                  </li>
                )}
              </ul>

              <DateField
                id='promote-date'
                label='Дата приёма'
                required
                value={дата}
                onChange={setДата}
                hint='С неё человек считается принятым и попадает в табель.'
              />

              {справочникВиден ? (
                <SelectField
                  id='promote-employment-type'
                  label='Тип оформления'
                  value={типОформления}
                  onChange={setТипОформления}
                  options={(типы.data?.items ?? []).map((тип) => ({
                    value: тип.id,
                    label: тип.title,
                  }))}
                  noneLabel='Проставим позже'
                  placeholder='Проставим позже'
                  pending={типы.isPending}
                  failed={типы.isError}
                  failedError={типы.error as ApiError | null}
                  emptyNote='Типов оформления в справочнике нет: их заводит администратор в настройках.'
                  hint='От типа зависит, какое основание нужно: по трудовому договору — заявление, по остальным довольно подтверждения кадров.'
                />
              ) : (
                <p className='text-paragraph-xs text-text-sub-600'>
                  Тип оформления проставляется в карточке сотрудника, в разделе «Люди»: справочник
                  типов открыт по праву на справочники, а у вашей учётной записи его нет. Оформлению
                  это не мешает.
                </p>
              )}

              <TextArea
                id='promote-confirmation'
                label='Подтверждение кадров'
                required={!заявлениеПриложено}
                value={подтверждение}
                onChange={setПодтверждение}
                placeholder='Например: принят по договору оказания услуг, заявление не требуется'
                error={ошибкаОснования}
                hint={
                  заявлениеПриложено
                    ? 'Заявление о приёме на работу уже приложено — подтверждение необязательно, но останется в журнале.'
                    : 'Заявление о приёме на работу не приложено. Без него нужно ваше подтверждение: «оформлен» без основания это человек, которого в реестре считают принятым, а в кадрах о нём ничего нет.'
                }
              />
            </div>
          )}

          {!готово && !заблокированСписком && (
            <p className='mt-3 text-paragraph-xs text-text-sub-600'>
              Часть требований на вкладке «Оформление» ещё не сошлась. Нажать можно — сервер скажет
              точную причину отказа, и она появится здесь же.
            </p>
          )}

          {мутация.isError && <ErrorAlert error={мутация.error} className='mt-3' />}

          {мутация.isSuccess && (
            <p className='mt-3 text-paragraph-sm text-text-strong-950'>
              {мутация.data.replay
                ? 'Этот человек уже был оформлен раньше — вернулась та же карточка сотрудника, второй не завелось.'
                : 'Готово: карточка сотрудника заведена. Найти её можно в разделе «Люди».'}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root variant='neutral' mode='stroke' size='small' className='w-full'>
              {заблокированСписком || мутация.isSuccess ? 'Понятно' : 'Отмена'}
            </Button.Root>
          </Modal.Close>
          {!заблокированСписком && !мутация.isSuccess && (
            <Button.Root
              type='button'
              variant='primary'
              mode='filled'
              size='small'
              className='w-full'
              onClick={оформить}
            >
              {мутация.isPending ? 'Оформляем…' : 'Оформить'}
            </Button.Root>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
